<template>
  <panel-layout
    id="integration-panel"
    :loading="loading"
    header-class="bg-gray-50 dark:bg-gray-900"
  >
    <template #heading>
      <div class="flex items-center sm:px-4 sm:py-3">
        <img :src="`${$config.public.api}/v1/integration/icon?guid=${integration.guid}&type=&size=256`" class="h-20 w-20 p-2 bg-white dark:bg-gray-950 rounded-xl border mr-5">

        <div>
          <h3 class="text-lg text-gray-800 dark:text-white font-semibold">
            {{ integration.name }}
          </h3>
          <p class="text-sm text-gray-600 dark:text-white/75">
            {{ integration.description }}
          </p>
          <div class="flex-wrap flex items-center mt-2">
            <o-chip v-if="isStatusConnected" variant="success" size="xs" class="mb-1 mr-1">
              {{ isAuthTypeNone ? $t('general.enabled') : $t('integration.connected') }}
            </o-chip>

            <o-chip v-if="isStatusSetupIncomplete" variant="warning" size="xs" class="mb-1 mr-1">
              {{ $t('integration.setupIncomplete') }}
            </o-chip>

            <o-chip v-if="isStatusAuthError" variant="danger" size="xs" class="mb-1 mr-1">
              {{ isAuthTypeNone ? $t('pages.integration.id.index.needsReenabling') : $t('pages.integration.id.index.needsReconnecting') }}
            </o-chip>
            <o-chip v-for="(category, index) in integration.categories" :key="index" size="xs" class="capitalize mb-1 mr-1">
              {{ category.replace('-', ' ') }}
            </o-chip>
          </div>
        </div>
      </div>
    </template>

    <div v-if="integration" class="h-full">
      <o-tabs :key="tabs.length + '-tabs'" nav-class="pl-3 sm:pl-8 bg-gray-50 border-b" container-class="bg-white dark:bg-gray-900 h-full" :show-arrows="false">
        <o-tab :name="$t('pages.integration.id.index.overview')">
          <div v-if="!allowIntegrations" class="px-10 pt-8">
            <o-alert show :title="$t('pages.integration.id.index.notVerified')" variant="info" class="text-sm">
              <p>
                {{ $t('pages.integration.id.index.onlyVerified') }}
              </p>

              <p>
                {{ $t('pages.integration.id.index.ifAlreadyVerified') }}
              </p>
            </o-alert>
          </div>

          <div v-if="isStatusSetupIncomplete" class="px-10 pt-8">
            <o-alert show :title="$t('pages.integration.id.index.notSetUp')" variant="danger" class="text-sm">
              <ul v-if="integration.setup && integration.setup.errors" class="list-disc ml-2 pl-2">
                <li v-for="(error, index) in integration.setup.errors" :key="index">
                  {{ error.join(', ') }}
                </li>
              </ul>
            </o-alert>
          </div>

          <div v-if="Object.keys(integration.setup?.warnings || {}).length" class="px-10 pt-8">
            <o-alert show variant="warning" class="text-sm">
              <ul class="list-disc ml-2 pl-2">
                <li v-for="(warning, index) in integration.setup.warnings" :key="index">
                  {{ Array.isArray(warning) ? warning.join(', ') : warning }}
                </li>
              </ul>
            </o-alert>
          </div>

          <overview :integration="integration" />
        </o-tab>
        <o-tab :is-visible="!!options" :name="$t('general.settings')">
          <settings v-model:options="options" :integration="integration" />
        </o-tab>

        <template v-if="isStatusConnected">
          <o-tab v-for="(tab, index) in tabs" :key="index" :name="tab.name">
            <component :is="tab.component" v-model:options="options" :integration="integration" @reconnect="handleReconnect" />
          </o-tab>
        </template>

        <template #trailing>
          <div class="flex items-center space-x-2 mb-2 mr-4">
            <actions v-if="isStatusConnected" :integration="integration" />

            <o-button v-if="isStatusConnected || isStatusSetupIncomplete" variant="danger" @click="handleDisconnect">
              {{ isAuthTypeNone ? $t('general.disable') : $t('pages.integration.id.index.disconnect') }}
            </o-button>
            <o-button v-if="isStatusDisconnected && allowIntegrations" variant="success" @click="handleConnect">
              {{ isAuthTypeNone ? $t('general.enable') : $t('pages.integration.id.index.connect') }}
            </o-button>
            <o-button v-if="isStatusAuthError" variant="warning" @click="handleReconnect">
              {{ isAuthTypeNone ? $t('pages.integration.id.index.reenable') : $t('pages.integration.id.index.reconnect') }}
            </o-button>
          </div>
        </template>
      </o-tabs>
    </div>

    <template #footer>
      <div v-if="!!options" class="flex justify-between">
        <o-button flat outline @click="handleCancel">
          {{ $t('general.cancel') }}
        </o-button>

        <o-button variant="success" @click="handleSave">
          {{ $t('general.save') }}
        </o-button>
      </div>
    </template>
  </panel-layout>
</template>
<script setup lang="ts">
import cloneDeep from 'lodash/cloneDeep'

// @ts-ignore
import integrations from '@/components/integration/edit/integrations'

import Overview from '@/components/integration/edit/Overview.vue'
import Settings from '@/components/integration/edit/Settings.vue'
// @ts-ignore
import Actions from '@/components/integration/edit/Actions.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import {
  OTabs,
  OTab,
  OAlert,
} from '@/components/elements'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $api, $notification, $bus, $auth, $i18n } = useNuxtApp()
const $config = useRuntimeConfig()

const integration = ref<any>({})
const tabs = ref<any[]>([])
const options = ref<any>(null)
const loading = ref(true)

const isStatusDisconnected = computed(() => integration.value.isStatusDisconnected)
const isStatusConnected = computed(() => integration.value.isStatusConnected)
const isStatusSetupIncomplete = computed(() => integration.value.isStatusSetupIncomplete)
const isStatusAuthError = computed(() => integration.value.isStatusAuthError)
const isAuthTypeNone = computed(() => integration.value.isAuthTypeNone)
const allowIntegrations = computed(() => $auth.info.location.allowIntegrations !== false)

const fetchIntegration = async () => {
  try {
    const response = await $api.integration.get(props.id as string)
    const _integration = response.data

    if (_integration) {
      if (integrations[_integration.guid]) {
        tabs.value = integrations[_integration.guid].tabs || []
      }

      integration.value = _integration
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }
}

const fetchOptions = async () => {
  const _integration = integration.value
  options.value = null

  if ((isStatusConnected.value || isStatusSetupIncomplete.value) && _integration.hasOptionsPage) {
    try {
      const _options = await $api.integration.getOptions(integration.value.guid)

      if (_options) {
        options.value = _options
      }
    } catch (e: any) {
      $notification.error({ errors: e?.errors })
    }
  }
}

const handleDisconnect = async () => {
  loading.value = true
  const _integration = integration.value

  try {
    const success = await $api.integration.unauthenticate(_integration.guid)

    if (success) {
      $notification.toast({
        title: $i18n.t('pages.integration.id.index.disconnectSuccess'),
        text: $i18n.t('pages.integration.id.index.disconnectSuccessInfo', { name: _integration.name }),
        variant: 'success',
      })

      await fetchIntegration()
      await fetchOptions()

      $bus.emit.updated('Integration', _integration)
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const openConnectPage = async (reconnect = false) => {
  const guid = integration.value?.guid
  if (!guid) return

  await panel.close(true)
  return navigateTo({
    path: `/integration/${guid}/connect`,
    query: {
      returnTo: '/account/integrations',
      ...(reconnect ? { reconnect: '1' } : {}),
    },
  })
}

const handleConnect = () => openConnectPage(false)

const handleSave = async () => {
  loading.value = true
  const _integration = integration.value
  const _options = cloneDeep(options.value)

  try {
    const data = await $api.integration.setOptions(_integration.guid, _options)

    if (data) {
      $notification.toast({
        title: $i18n.t('general.saved'),
        text: $i18n.t('pages.integration.id.index.savedSuccessInfo', { name: _integration.name }),
        variant: 'success',
      })
      options.value = data

      fetchIntegration()
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const handleCancel = () => {
  panel.close()
}

const handleReconnect = () => openConnectPage(true)

onMounted(async () => {
  await fetchIntegration()
  await fetchOptions()

  loading.value = false
})
</script>
